import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, Send, Unplug, BellRing } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Field } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { WaTemplates, WaAutomation } from "./WaManager";

const KINDS = [["confirmation", "Confirmation template"], ["reminder", "Reminder template"], ["job_alert", "Practitioner job alert template (optional)"], ["rating_request", "Patient rating request template (optional)"], ["aftercare", "Aftercare template (optional)"], ["followup", "Follow-up check-in template (optional)"]];
const DEFAULT_TPL = { confirmation: { en: { name: "appointment_confirmation", code: "en" }, it: { name: "appointment_confirmation", code: "it" } },
  reminder: { en: { name: "appointment_reminder", code: "en" }, it: { name: "appointment_reminder", code: "it" } } };

function CopyRow({ label, value, testId }) {
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-xs" data-testid={testId} />
        <Button variant="outline" type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
      </div>
    </Field>
  );
}

export function WhatsAppSettings({ onChanged }) {
  const [wa, setWa] = useState(null);
  const [f, setF] = useState({ phone_number_id: "", access_token: "", app_secret: "", waba_id: "", templates: DEFAULT_TPL });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/whatsapp").then((r) => {
    setWa(r.data);
    setF((p) => ({ ...p, waba_id: p.waba_id || r.data.waba_id || "" }));
    if (r.data.templates?.reminder) setF((p) => ({ ...p, templates: { ...DEFAULT_TPL, ...r.data.templates } }));
  });
  useEffect(() => { load(); }, []);

  const setT = (kind, lang, k, v) => setF({ ...f, templates: { ...f.templates, [kind]: { ...f.templates[kind], [lang]: { ...(f.templates[kind]?.[lang] || {}), [k]: v } } } });
  const connect = async () => {
    setBusy(true);
    try { await api.put("/settings/whatsapp", f); toast.success("WhatsApp connected"); setF({ ...f, access_token: "", app_secret: "" }); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect WhatsApp? Automatic messages will stop.")) return;
    await api.delete("/settings/whatsapp"); toast.success("Disconnected"); load(); onChanged?.();
  };
  const test = async () => {
    try { await api.post("/settings/whatsapp/test", { to: testTo }); toast.success("Test reminder sent"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!wa) return null;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-8">
      <div className="space-y-5">
        <div data-testid="whatsapp-status" className={`flex items-center gap-3 rounded-[var(--radius)] border p-4 text-sm ${wa.connected ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-800" : "bg-muted/40"}`}>
          {wa.connected ? <><CheckCircle2 className="h-5 w-5" />Connected {wa.display_phone && `· ${wa.display_phone}`} · ID …{wa.phone_number_id_last4}</>
            : "Not connected. Confirmations and reminders are sent by email only; the WhatsApp button opens a pre-filled chat."}
          {wa.connected && <Button size="sm" variant="ghost" className="ml-auto" data-testid="whatsapp-disconnect-btn" onClick={disconnect}><Unplug className="h-4 w-4 mr-1" />Disconnect</Button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone number ID"><Input data-testid="whatsapp-phone-id-input" value={f.phone_number_id} placeholder={wa.connected ? `…${wa.phone_number_id_last4}` : "e.g. 1234567890"} onChange={(e) => setF({ ...f, phone_number_id: e.target.value })} /></Field>
          <Field label={`Access token${wa.connected ? " (leave blank to keep)" : ""}`}><Input type="password" data-testid="whatsapp-token-input" value={f.access_token} onChange={(e) => setF({ ...f, access_token: e.target.value })} /></Field>
          <Field label="WhatsApp Business Account ID (needed to manage templates from Bloomwell)" className="sm:col-span-2"><Input data-testid="whatsapp-waba-input" value={f.waba_id} placeholder="e.g. 102345678901234" onChange={(e) => setF({ ...f, waba_id: e.target.value.replace(/\D/g, "") })} /></Field>
          <Field label={`App secret (optional, verifies status webhooks)${wa.has_app_secret ? " · saved" : ""}`} className="sm:col-span-2"><Input type="password" data-testid="whatsapp-secret-input" value={f.app_secret} onChange={(e) => setF({ ...f, app_secret: e.target.value })} /></Field>
        </div>
        {KINDS.map(([kind, label]) => (
          <div key={kind} className="rounded-[var(--radius)] border p-4 space-y-3">
            <p className="text-sm font-semibold">{label}</p>
            {["en", "it"].map((lang) => (
              <div key={lang} className="grid grid-cols-[40px_1fr_110px] gap-3 items-center">
                <span className="text-xs font-semibold uppercase">{lang}</span>
                <Input data-testid={`whatsapp-${kind}-${lang}-name`} placeholder="approved_template_name" value={f.templates[kind]?.[lang]?.name || ""} onChange={(e) => setT(kind, lang, "name", e.target.value)} />
                <Input data-testid={`whatsapp-${kind}-${lang}-code`} placeholder="en_US" value={f.templates[kind]?.[lang]?.code || ""} onChange={(e) => setT(kind, lang, "code", e.target.value)} />
              </div>
            ))}
          </div>
        ))}
        <Button className="rounded-full" data-testid="whatsapp-connect-btn" disabled={busy || f.phone_number_id.length < 5 || (!wa.connected && !f.access_token)} onClick={connect}>
          {wa.connected ? "Update connection" : "Save and connect"}
        </Button>
        {wa.connected && (
          <div className="flex gap-2 max-w-md">
            <Input data-testid="whatsapp-test-to" placeholder="+44 7700 900000" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <Button variant="outline" data-testid="whatsapp-test-btn" disabled={!testTo} onClick={test}><Send className="h-4 w-4 mr-1.5" />Send test</Button>
          </div>
        )}
        {wa.connected && <div className="border-t pt-6"><WaTemplates wabaId={wa.waba_id} /></div>}
        {wa.connected && <div className="border-t pt-6"><WaAutomation /></div>}
      </div>
      <div className="space-y-4 text-sm text-muted-foreground">
        <div className="rounded-[var(--radius)] border bg-card p-5 space-y-2">
          <p className="font-semibold text-foreground">How to connect</p>
          <ol className="list-decimal pl-4 space-y-1.5">
            <li>In Meta for Developers, add WhatsApp to your app and copy the <b>Phone number ID</b> and the <b>WhatsApp Business Account ID</b>.</li>
            <li>Create a permanent System User token with <i>whatsapp_business_messaging</i> and <i>whatsapp_business_management</i>.</li>
            <li>Save and connect, then click <b>Create booking templates for me</b>: Bloomwell sends the confirmation and reminder templates (EN + IT) to Meta and fills in the names below.</li>
            <li>Optional: an <b>aftercare</b> template ({"{{1}}"} first name, {"{{2}}"} treatment, {"{{3}}"} clinic, {"{{4}}"} link to the full aftercare in the patient portal) and a <b>follow-up</b> template ({"{{1}}"} first name, {"{{2}}"} treatment, {"{{3}}"} clinic, {"{{4}}"} book-again link).</li>
            <li>Optional: a <b>job alert</b> template for practitioners ({"{{1}}"} name, {"{{2}}"} treatment, {"{{3}}"} date & time, {"{{4}}"} area & reply link) and a <b>rating request</b> template ({"{{1}}"} first name, {"{{2}}"} treatment, {"{{3}}"} practitioner, {"{{4}}"} rating link).</li>
            <li>Enter the exact template names and language codes (e.g. en_US / it) here.</li>
            <li>Add the webhook below in Meta (subscribe to <b>messages</b>) so patient replies arrive in the Inbox and delivered/read status is tracked.</li>
          </ol>
          <p>Only clients with <b>WhatsApp opt-in</b> on their profile receive messages.</p>
        </div>
        <CopyRow label="Webhook callback URL" value={wa.webhook_url} testId="whatsapp-webhook-url" />
        <CopyRow label="Verify token" value={wa.verify_token} testId="whatsapp-verify-token" />
      </div>
    </div>
  );
}

export function RemindersSettings({ value, onChange, whatsappConnected }) {
  const r = { enabled: true, hours_before: 24, email: true, whatsapp: true, ...value };
  const set = (k, v) => onChange({ ...r, [k]: v });
  const runNow = async () => {
    try {
      const { data } = await api.post("/reminders/run");
      toast.success(`Reminders: ${data.email} email, ${data.whatsapp} WhatsApp (${data.checked} due)`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-start gap-3 text-sm text-muted-foreground"><BellRing className="h-5 w-5 text-primary shrink-0" />
        <p>Reminders are checked automatically every 15 minutes and sent once per appointment to confirmed bookings. Bookings made inside the reminder window are skipped (they just received their confirmation). Save settings after changes.</p></div>
      <label className="flex items-center gap-3 text-sm"><Switch data-testid="reminders-enabled-switch" checked={r.enabled} onCheckedChange={(v) => set("enabled", v)} />Send automatic reminders</label>
      <Field label="Send reminder this many hours before the appointment">
        <Input type="number" min="1" max="168" className="max-w-[160px]" data-testid="reminders-hours-input" value={r.hours_before} onChange={(e) => set("hours_before", Number(e.target.value))} />
      </Field>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-3 text-sm"><Switch data-testid="reminders-email-switch" checked={r.email} onCheckedChange={(v) => set("email", v)} />Email</label>
        <label className="flex items-center gap-3 text-sm"><Switch data-testid="reminders-whatsapp-switch" checked={r.whatsapp} onCheckedChange={(v) => set("whatsapp", v)} />
          WhatsApp {!whatsappConnected && <span className="text-xs text-muted-foreground">(not connected)</span>}</label>
      </div>
      <Button variant="outline" className="rounded-full" data-testid="reminders-run-now-btn" onClick={runNow}><Send className="h-4 w-4 mr-1.5" />Send due reminders now</Button>
    </div>
  );
}
